/**
 * Pure helpers for the Verified Documents verifier.
 *
 * - `sha256Hex(buffer)` — Web Crypto API wrapper that produces the hex
 *   digest without ever reaching for the network. Falls back to a plain
 *   TypeError if the browser lacks SubtleCrypto (e.g. non-HTTPS context).
 * - `computeMatchState(hashHex, documents)` — pure function; the unit
 *   test file imports this directly.
 */

/* global crypto */

export async function sha256Hex(arrayBuffer) {
  if (!crypto || !crypto.subtle || !crypto.subtle.digest) {
    throw new TypeError(
      "SubtleCrypto is unavailable in this browser context."
    );
  }
  const digest = await crypto.subtle.digest("SHA-256", arrayBuffer);
  const bytes = new Uint8Array(digest);
  const hex = [];
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i].toString(16).padStart(2, "0");
    hex.push(b);
  }
  return hex.join("");
}

/**
 * Match the user's hex digest against the published document list.
 * Three outcomes:
 *   - { state: 'green',  matched, current }
 *       the file matches the current (not superseded) document
 *   - { state: 'amber',  matched, current }
 *       the file matches a superseded version; `current` is the newest
 *       un-superseded document when one exists
 *   - { state: 'red',    hash }
 *       no document in the list has this fingerprint
 *
 * `documents` is the raw array from `GET /api/documents`.
 */
export function computeMatchState(hashHex, documents) {
  if (!hashHex || typeof hashHex !== "string") {
    return { state: "red", hash: hashHex || "" };
  }
  const target = hashHex.toLowerCase();
  const safeDocs = Array.isArray(documents) ? documents : [];
  const match = safeDocs.find((d) => (d.sha256 || "").toLowerCase() === target);
  if (!match) {
    return { state: "red", hash: target };
  }
  if (match.status === "current") {
    return { state: "green", matched: match };
  }
  const current = safeDocs.find((d) => d.status === "current") || null;
  return { state: "amber", matched: match, current };
}

/**
 * Short display form of a 64-char hex digest.
 * "d48a05cc94964099…fa3ac44b5" — easy to scan, easy to copy from.
 */
export function abbrevHash(hex) {
  if (!hex || hex.length < 20) return hex || "";
  return `${hex.slice(0, 10)}…${hex.slice(-9)}`;
}

export const PDF_ACCEPT = "application/pdf,.pdf";
export const PDF_EXT_RE = /\.pdf$/i;

export function isPdfFile(file) {
  if (!file) return false;
  const type = (file.type || "").toLowerCase();
  if (type === "application/pdf") return true;
  return PDF_EXT_RE.test(file.name || "");
}
